import { UNIVERSAL_ENV_VARS } from '../../../constants/environmentVariables';
import {
  STATIC_MAPS_CATCH_POSITIONS_ZOOM_LEVEL,
  STATIC_MAPS_FISHING_WATERS_ZOOM_LEVEL,
} from '../constants/constants';

export type StaticMapType = 'water' | 'secret_water' | 'catch' | 'place' | 'default';

export const MAPBOX_STYLES = {
  satellite: 'fishbrain-dev/cizpsorb700hh2ro1g0ghrcc1',
  hybrid: 'fishbrain-dev/cj459rh840a4d2ro7xdlcr411',
  terrain: 'fishbrain-dev/cj3ve0eew02fi2smkj52shgo2',
} as const;
export type MapStyle = keyof typeof MAPBOX_STYLES;
const MAPBOX_MAX_WIDTH = 1280;
const MAPBOX_MAX_HEIGHT = 1280;
const ENDPOINT = 'https://api.mapbox.com/styles/v1';
const DEFAULT_QUERY = `access_token=${UNIVERSAL_ENV_VARS.MAPBOX_TOKEN}&logo=false&attribution=false`;

export function getStaticMap({
  longitude,
  latitude,
  width,
  height,
  style = 'terrain',
  overlay,
  zoomLevel = STATIC_MAPS_FISHING_WATERS_ZOOM_LEVEL,
  imageUrl,
  highPixelRatio = false,
}: {
  longitude: string;
  latitude: string;
  width: number;
  height: number;
  style?: keyof typeof MAPBOX_STYLES;
  overlay?: string;
  zoomLevel?: number;
  imageUrl?: string;
  highPixelRatio?: boolean;
}): string {
  const newWidth = width > MAPBOX_MAX_WIDTH ? MAPBOX_MAX_WIDTH : width;
  const newHeight = height > MAPBOX_MAX_HEIGHT ? MAPBOX_MAX_HEIGHT : height;
  const baseUrl = `${ENDPOINT}/${MAPBOX_STYLES[style]}/static`;
  const mapValues = `${longitude},${latitude},${zoomLevel},0.00,0.00/${newWidth}x${newHeight}${
    highPixelRatio ? '@2x' : ''
  }`;
  if (imageUrl) {
    return `${baseUrl}/url-${imageUrl}(${longitude},${latitude})/${mapValues}?${DEFAULT_QUERY}`;
  }
  if (overlay) {
    return `${baseUrl}/geojson(${overlay})/${mapValues}?${DEFAULT_QUERY}`;
  }
  return `${baseUrl}/${mapValues}?${DEFAULT_QUERY}`;
}

export function getStaticWaterMap(
  longitude: string,
  latitude: string,
  width: number,
  height: number,
  style: MapStyle = 'terrain',
  highPixelRatio = false,
  zoomLevel = STATIC_MAPS_FISHING_WATERS_ZOOM_LEVEL,
): string {
  return getStaticMap({
    longitude,
    latitude,
    width,
    height,
    style,
    zoomLevel,
    imageUrl: 'https%3A%2F%2Ffishbrain.com%2Fimages%2FmapboxStatic%2Fwater.png',
    highPixelRatio,
  });
}

export function getSecretStaticWaterMap(
  longitude: string,
  latitude: string,
  width: number,
  height: number,
  style: MapStyle = 'terrain',
  highPixelRatio = false,
): string {
  return getStaticMap({
    longitude,
    latitude,
    width,
    height,
    style,
    zoomLevel: STATIC_MAPS_FISHING_WATERS_ZOOM_LEVEL,
    highPixelRatio,
  });
}

export function getStaticCatchMap(
  longitude: string,
  latitude: string,
  width: number,
  height: number,
  style: keyof typeof MAPBOX_STYLES = 'terrain',
  highPixelRatio = false,
): string {
  return getStaticMap({
    longitude,
    latitude,
    width,
    height,
    style,
    zoomLevel: STATIC_MAPS_CATCH_POSITIONS_ZOOM_LEVEL,
    imageUrl: 'https%3A%2F%2Ffishbrain.com%2Fimages%2FmapboxStatic%2FcatchMarker.png',
    highPixelRatio,
  });
}

export function getStaticPlaceMap(
  longitude: string,
  latitude: string,
  width: number,
  height: number,
  style: keyof typeof MAPBOX_STYLES = 'terrain',
  highPixelRatio = false,
): string {
  return getStaticMap({
    longitude,
    latitude,
    width,
    height,
    style,
    zoomLevel: STATIC_MAPS_FISHING_WATERS_ZOOM_LEVEL,
    imageUrl: 'https%3A%2F%2Ffishbrain.com%2Fimages%2FmapboxStatic%2FplaceMarker.png',
    highPixelRatio,
  });
}
